/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */

import React from 'react';
import _ from 'lodash';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart,
  Line,
  LineChart,
  Surface,
  Symbols,
  // Label,
  LabelList,
} from 'recharts';

import { numberWithCommas, DataFormater, abbreviateThousands } from 'helper';

const barDefault = {
  xAxisProps: {
    dataKey: 'name',
    textAnchor: 'end',
    verticalAnchor: 'end',
    scaleToFit: false,
    interval: 0,
    fontSize: '10px',
    // scale: 'auto',
  },
  yAxisProps: {
    fontSize: '10px',
  },
};

const PielegendtDefaultOption = {
  layout: 'horizontal',
  verticalAlign: 'top',
  align: 'center',
};

const COLORS = ['#1cd9ff', '#ad6d57', '#FFBB28', '#FF8042'];
// const randomColors = () => `#${Math.floor(Math.random() * 16777215).toString(16)}`;

const randomColors = () => {
  const letters = '34567abcdfe'.split('');
  let color = '#';
  for (let i = 0; i < 6; i += 1) {
    color += letters[Math.floor(Math.random() * letters.length)];
  }

  return color;
};

const RADIAN = Math.PI / 180;
const renderCustomizeLabelForPie = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export default (WrappedComponent) =>
  class withDashboard extends React.Component {
    
    state = {
      _key: '1',
    };

  
    createCountCard = (label, value, icon = '') => (
      <div className="card card-base dashboard-card">
        <div className="icon-represent">{icon}</div>
        <div className="count-title">{label}</div>
        <div className="count">{numberWithCommas(value)}</div>
      </div>
    );

    createSingleAreaChart = (data, key, options = {}, isLoading = false) => (
      <>
        {isLoading && (
          <div
            className="text-center"
            style={{
              position: 'absolute',
              zIndex: 5,
              left: 0,
              right: 0,
              margin: '60px auto',
              width: 40,
              height: 40,
              background: '#eaeaea',
              borderRadius: '50%',
              paddingTop: 6,
              fontWeight: 'bolder',
              fontSize: 20,
            }}
          >
            <span className="fas fa-1x fa-spin fa-spinner" />
          </div>
        )}
        <ResponsiveContainer width="100%" height={options.size || 450}>
          <ComposedChart
            data={data}
            margin={{
              top: 20,
              right: 45,
              left: -10,
              bottom: 50,
              ...options.composeOption,
            }}
          >
            <YAxis {...barDefault.yAxisProps} {...options.yAxisProps} />
            <XAxis {...barDefault.xAxisProps} {...options.xAxisProps} />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active) {
                  return (
                    <div className="bg-light p-2">
                      <p className="font-weight-bold mb-0">{`${label}`}</p>
                      <p className="mb-0">
                        {`${_.get(payload, '0.dataKey')}`}:{' '}
                        {numberWithCommas(_.get(payload, '0.value', 0))}
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              verticalAlign="top"
              height={36}
              content={(props) => (
                <div
                  style={{
                    textAlign: props.align,
                    marginTop: -5,
                    marginLeft: 50,
                  }}
                >
                  <span className="legend-item">
                    <Surface width={10} height={10}>
                      <Symbols
                        cx={5}
                        cy={5}
                        type="circle"
                        size={50}
                        fill={_.get(props, 'payload.1.color', '#1cd9ff')}
                      />
                    </Surface>
                    <small className="ml-1 font-weight-bold text-black-50">
                      {key}
                    </small>
                  </span>
                </div>
              )}
            />
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
            {data.length > 0 && (
              <Area
                dataKey={key}
                type="monotone"
                fill="#b5b5b5"
                stroke={null}
                id="area"
                animationEasing="linear"
                fillOpacity={0.5}
                animationDuration={800}
                animationBegin={200}
              />
            )}
            {data.length > 0 && (
              <Line
                dataKey={key}
                type="monotone"
                stroke="#1cd9ff"
                fill="#1cd9ff"
                activeDot={{ r: 8 }}
                strokeWidth={2}
                animationEasing="linear"
                animationDuration={800}
                animationBegin={200}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </>
    );

    createDynamicAreaChart = (
      data = [],
      key = [],
      options = {},
      isLoading = false
    ) => (
      <>
        {isLoading && (
          <div
            className="text-center"
            style={{
              position: 'absolute',
              zIndex: 5,
              left: 0,
              right: 0,
              margin: '60px auto',
              width: 40,
              height: 40,
              background: '#eaeaea',
              borderRadius: '50%',
              paddingTop: 6,
              fontWeight: 'bolder',
              fontSize: 20,
            }}
          >
            <span className="fas fa-1x fa-spin fa-spinner" />
          </div>
        )}
        <ResponsiveContainer width="100%" height={options.size || 400}>
          <ComposedChart
            data={data}
            margin={{
              top: 20,
              right: 25,
              left: -10,
              bottom: 50,
              ...options.composeOption,
            }}
          >
            <YAxis
              tickFormatter={DataFormater}
              {...barDefault.yAxisProps}
              {...options.yAxisProps}
            />
            <XAxis {...barDefault.xAxisProps} {...options.xAxisProps} />
            {!isLoading && (
              <Tooltip
                content={({ active, payload, label }) => {
                  // const t = _.orderBy(_.uniqBy(payload, 'dataKey'), ['dataKey'], ['desc']);
                  const t = _.uniqBy(payload, 'dataKey');
                  if (active) {
                    return (
                      <div className="bg-light p-2">
                        <p className="font-weight-bold mb-0">{`${label}`}</p>
                        {t &&
                          t.map((item) => (
                            <p
                              key={item.payload.month}
                              className="mb-0 font-weight-bold"
                              style={{
                                color: _.get(item, 'fill'),
                              }}
                            >
                              {`${_.get(item, 'dataKey')}`}:{' '}
                              {abbreviateThousands(_.get(item, 'value', 0))}
                            </p>
                          ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />
            )}
            {!isLoading && (
              <Legend
                verticalAlign="top"
                height={36}
                content={({ payload, align }) => {
                  // const t = _.orderBy(_.uniqBy(payload, 'dataKey'), ['dataKey'], ['desc']);
                  const t = _.uniqBy(payload, 'dataKey');
                  return (
                    <div
                      style={{
                        textAlign: align,
                        marginTop: -5,
                        marginLeft: 50,
                      }}
                    >
                      {t &&
                        t.map((item) => (
                          <span
                            key={_.get(item, 'dataKey')}
                            className="legend-item mr-1"
                          >
                            <Surface width={10} height={10} className="">
                              <Symbols
                                cx={5}
                                cy={5}
                                type="circle"
                                size={50}
                                fill={_.get(item, 'payload.fill', '#1cd9ff')}
                              />
                            </Surface>
                            <small className="ml-1 font-weight-bold text-black-50">
                              {_.get(item, 'value')}
                            </small>
                          </span>
                        ))}
                    </div>
                  );
                }}
              />
            )}
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
            {!isLoading &&
              key.map((item) => (
                <Area
                  key={item.key}
                  dataKey={item.key}
                  type="linear"
                  fill={item.color}
                  stroke={null}
                  animationEasing="linear"
                  fillOpacity={0.3}
                  animationDuration={800}
                  animationBegin={200}
                />
              ))}
            {!isLoading &&
              key.map((item) => (
                <Line
                  key={item.key}
                  dataKey={item.key}
                  valueKey={item.valueKey}
                  type="linear"
                  stroke={`${item.color}`}
                  fill={`${item.color}`}
                  // activeDot={{ r: 8 }}
                  strokeWidth={2}
                  animationEasing="linear"
                  animationDuration={800}
                  animationBegin={200}
                />
              ))}
          </ComposedChart>
        </ResponsiveContainer>
      </>
    );

    // for new areachart dashboard
    createDynamicAreaChartVersion2 = (
      data,
      key = [],
      options = {},
      isLoading = false
    ) => {
      const charSpinner = isLoading && (
        <div
          className="text-center"
          style={{
            position: 'absolute',
            zIndex: 5,
            left: 0,
            right: 0,
            margin: '60px auto',
            width: 40,
            height: 40,
            background: '#eaeaea',
            borderRadius: '50%',
            paddingTop: 6,
            fontWeight: 'bolder',
            fontSize: 20,
          }}
        >
          <span className="fas fa-1x fa-spin fa-spinner" />
        </div>
      );
      return (
        <>
          {charSpinner}
          <ResponsiveContainer width="100%" height={options.size || 400}>
            <AreaChart
              data={data}
              margin={{
                top: 20,
                right: 25,
                left: -10,
                bottom: 50,
                ...options.composeOption,
              }}
            >
              {/* <YAxis
                tickFormatter={DataFormater}
                {...barDefault.yAxisProps}
                {...options.yAxisProps}
              />
              <XAxis {...barDefault.xAxisProps} {...options.xAxisProps} /> */}
              {/* <Tooltip
                content={({ active, payload, label }) => {
                  if (active) {
                    return (
                      <div className="bg-light p-2">
                        <p className="font-weight-bold mb-0">{`${label}`}</p>
                        <p className="mb-0 title-color">
                          {`${_.get(payload, '0.dataKey')}`}:{' '}
                          {numberWithCommas(_.get(payload, '0.value', 0).toFixed(2))}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              /> */}
              {/* <Legend /> */}
              <CartesianGrid
                // strokeDasharray="4"
                opacity="0.3"
                vertical={false}
              />
              {/* {!isLoading &&
                key.map((item) => (
                  <defs key={item.key}>
                    <linearGradient id="baseColor" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={item.color}
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="#7790CB00"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                ))} */}
              {!isLoading &&
                key.map((item) => (
                  <Area
                    key={item.key}
                    dataKey="collection"
                    // dot={{ stroke: item.color, strokeWidth: 3 }}
                    type="linear"
                    fill="url(#baseColor)"
                    stackId="1"
                    stroke={item.color}
                    strokeWidth={4}
                    fillOpacity={1}
                    animationEasing="linear"
                    animationDuration={800}
                    animationBegin={200}
                  />
                ))}
            </AreaChart>
          </ResponsiveContainer>
        </>
      );
    };

    // for new areachart dashboard
    createDynamicLineChart = (
      data,
      key = [],
      options = {},
      isLoading = false
    ) => {
      const charSpinner = isLoading && (
        <div
          className="text-center"
          style={{
            position: 'absolute',
            zIndex: 5,
            left: 0,
            right: 0,
            margin: '60px auto',
            width: 40,
            height: 40,
            background: '#eaeaea',
            borderRadius: '50%',
            paddingTop: 6,
            fontWeight: 'bolder',
            fontSize: 20,
          }}
        >
          <span className="fas fa-1x fa-spin fa-spinner" />
        </div>
      );
      return (
        <>
          {charSpinner}
          <ResponsiveContainer width="100%" height={options.size || 400}>
            <LineChart
              data={data}
              margin={{
                top: 20,
                right: 25,
                left: -10,
                bottom: 50,
                ...options.composeOption,
              }}
            >
              {/* <YAxis
                tickFormatter={DataFormater}
                {...barDefault.yAxisProps}
                {...options.yAxisProps}
              />
              <XAxis {...barDefault.xAxisProps} {...options.xAxisProps} /> */}
              {/* <Tooltip className="rounded-circle" /> */}
              {/* <Legend /> */}
              <CartesianGrid opacity="0.3" vertical={false} />
              {!isLoading &&
                key.map((item) => (
                  <Line
                    dot={false}
                    activeDot={false}
                    type="monotone"
                    key={item.key}
                    dataKey={item.key}
                    stackId="1"
                    stroke={item.color}
                    strokeWidth={1}
                    animationEasing="linear"
                    animationDuration={800}
                    animationBegin={200}
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </>
      );
    };

    createBiaxialBarChart = (
      data,
      key = [],
      options = {},
      isLoading = false
    ) => (
      <>
        {isLoading && (
          <div
            className="text-center"
            style={{
              position: 'absolute',
              zIndex: 5,
              left: 0,
              right: 0,
              margin: '60px auto',
              width: 40,
              height: 40,
              background: '#eaeaea',
              borderRadius: '50%',
              paddingTop: 6,
              fontWeight: 'bolder',
              fontSize: 20,
            }}
          >
            <span className="fas fa-1x fa-spin fa-spinner" />
          </div>
        )}
        <ResponsiveContainer width="100%" height={options.size || 450}>
          <ComposedChart
            data={data}
            margin={{
              top: 20,
              right: 20,
              left: -10,
              bottom: 40,
              ...options.composeOption,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...barDefault.xAxisProps} {...options.xAxisProps} />
            {key.map((item, i) => (
              <YAxis
                key={item.key}
                yAxisId={item.key}
                orientation={`${i % 2 === 0 ? 'left' : 'right'}`}
                stroke={`${i % 2 === 0 ? '#1cd9ff' : '#ffa01c'}`}
                {...barDefault.yAxisProps}
                {...options.yAxisProps}
              />
            ))}
            <Tooltip />
            <Legend verticalAlign="top" height={36} />
            {key.map((item, i) => (
              <Bar
                key={item.key}
                yAxisId={item.key}
                dataKey={item.key}
                fill={`${i % 2 === 0 ? '#1cd9ff' : '#ffa01c'}`}
                background={{
                  fill: `${i % 2 === 0 ? '#ddda' : '#eeea'}`,
                }}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </>
    );

    createSingleBarChart = (data, key, options = {}, isLoading = false) => (
      <>
        {isLoading && (
          <div
            className="text-center"
            style={{
              position: 'absolute',
              zIndex: 5,
              left: 0,
              right: 0,
              margin: '60px auto',
              width: 40,
              height: 40,
              background: '#eaeaea',
              borderRadius: '50%',
              paddingTop: 6,
              fontWeight: 'bolder',
              fontSize: 20,
            }}
          >
            <span className="fas fa-1x fa-spin fa-spinner" />
          </div>
        )}
        <ResponsiveContainer width="100%" height={options.size || 450}>
          <BarChart
            data={data}
            margin={{
              top: 20,
              right: 20,
              left: -10,
              bottom: 40,
            }}
            barCategoryGap={2}
          >
            <YAxis {...barDefault.yAxisProps} {...options.yAxisProps} />
            <XAxis {...barDefault.xAxisProps} {...options.xAxisProps} />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active) {
                  return (
                    <div className="bg-light p-2">
                      <p className="font-weight-bold mb-0">{`${label}`}</p>
                      <p className="mb-0">
                        {`${_.get(payload, '0.dataKey')}`}:{' '}
                        {numberWithCommas(_.get(payload, '0.value', 0))}
                      </p>
                    </div>
                  );
                }
                return null;
              }}
              cursor={{
                fill: '#eff0f1',
              }}
            />
            <Legend
              verticalAlign="top"
              height={36}
              content={(props) => (
                <div
                  style={{
                    textAlign: props.align,
                    marginTop: -5,
                    marginLeft: 50,
                  }}
                >
                  <span className="legend-item">
                    <Surface width={10} height={10}>
                      <Symbols
                        cx={5}
                        cy={5}
                        type="square"
                        size={50}
                        fill={_.get(props, 'payload.0.color', '#1cd9ff')}
                      />
                    </Surface>
                    <small className="ml-1 font-weight-bold text-black-50">
                      {key}
                    </small>
                  </span>
                </div>
              )}
            />
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
            <Bar
              dataKey={key}
              fill={options.fill}
              background={
                options.background && {
                  fill: options.background,
                }
              }
              animationEasing="linear"
              animationDuration={800}
              animationBegin={200}
            />
          </BarChart>
        </ResponsiveContainer>
      </>
    );

    createDynamicBarChart = (data, key, options = {}, isLoading = false) => (
      <>
        {isLoading && (
          <div
            className="text-center"
            style={{
              position: 'absolute',
              zIndex: 5,
              left: 0,
              right: 0,
              margin: '60px auto',
              width: 40,
              background: '#eaeaea',
              borderRadius: '50%',
              paddingTop: 6,
              fontWeight: 'bolder',
              fontSize: 20,
            }}
          >
            <span className="fas fa-1x fa-spin fa-spinner" />
          </div>
        )}
        <ResponsiveContainer width="100%" height={options.size || 450}>
          <BarChart
            data={data}
            margin={{
              top: 20,
              right: 25,
              left: -10,
              bottom: 50,
              ...options.composeOption,
            }}
          >
            <YAxis {...barDefault.yAxisProps} {...options.yAxisProps} />
            <XAxis {...barDefault.xAxisProps} {...options.xAxisProps} />
            {!isLoading && (
              <Tooltip
                content={({ active, payload, label }) => {
                  const t = _.orderBy(
                    _.uniqBy(payload, 'dataKey'),
                    ['dataKey'],
                    ['desc']
                  );
                  if (active) {
                    return (
                      <div className="bg-light p-2">
                        <p className="font-weight-bold mb-0">{`${label}`}</p>
                        {t &&
                          t.map((item) => (
                            <p
                              key={item.payload.month}
                              className="mb-0 font-weight-bold"
                              style={{
                                color: _.get(item, 'fill'),
                              }}
                            >
                              {`${_.get(item, 'dataKey')}`}:{' '}
                              {numberWithCommas(_.get(item, 'value', 0))}
                            </p>
                          ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />
            )}
            {!isLoading && (
              <Legend
                verticalAlign="bottom"
                height={70}
                content={({ payload, align }) => {
                  const t = _.orderBy(
                    _.uniqBy(payload, 'dataKey'),
                    ['dataKey'],
                    ['asc']
                  );
                  return (
                    <div
                      style={{
                        textAlign: align,
                        marginTop: 20,
                        marginLeft: 50,
                      }}
                    >
                      {t &&
                        t.map((item) => (
                          <span
                            key={_.get(item, 'dataKey')}
                            className="legend-item mr-1"
                          >
                            <Surface width={10} height={10}>
                              <Symbols
                                cx={5}
                                cy={5}
                                type="square"
                                size={50}
                                fill={_.get(item, 'payload.fill', '#1cd9ff')}
                              />
                            </Surface>
                            <small className="ml-1 font-weight-bold text-black-50">
                              {_.get(item, 'value')}
                            </small>
                          </span>
                        ))}
                    </div>
                  );
                }}
              />
            )}
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
            {!isLoading &&
              key.map((item) => (
                <Bar
                  key={item.key}
                  dataKey={item.key}
                  fill={item.color}
                  animationEasing="linear"
                  animationDuration={800}
                  animationBegin={200}
                />
              ))}
          </BarChart>
        </ResponsiveContainer>
      </>
    );

    render() {
      const { _key } = this.state;
      return (
        <WrappedComponent
          key={_key}
          {...this.props}
          createCountCard={this.createCountCard}
          createSingleAreaChart={this.createSingleAreaChart}
          createDynamicAreaChart={this.createDynamicAreaChart}
          createDynamicLineChart={this.createDynamicLineChart}
          createDynamicAreaChartVersion2={this.createDynamicAreaChartVersion2}
          createSingleBarChart={this.createSingleBarChart}
          createDynamicBarChart={this.createDynamicBarChart}
          createBiaxialBarChart={this.createBiaxialBarChart}
        />
      );
    }
  };
